import React from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FiArrowLeft, FiHome, FiEdit2, FiTrash2 } from 'react-icons/fi';

export default function UnitShow({ unit, user }) {
  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };
  
  // Format room type for display
  const formatRoomType = (roomType) => {
    return roomType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button variant="outline" className="mb-4" asChild>
              <Link href="/facility_owner/units">
                <FiArrowLeft className="mr-2 h-4 w-4" />
                Back to Units
              </Link>
            </Button>
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {formatRoomType(unit.room_type)}
                </h1>
                <p className="text-gray-500">
                  {unit.facility?.name || 'Unknown facility'}
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex space-x-3">
                <Button variant="outline" asChild>
                  <Link href={`/facility_owner/units/${unit.id}/edit`}>
                    <FiEdit2 className="mr-2 h-4 w-4" />
                    Edit Unit
                  </Link>
                </Button>
                <Button variant="destructive" asChild>
                  <Link 
                    href={`/facility_owner/units/${unit.id}`} 
                    method="delete" 
                    as="button"
                  >
                    <FiTrash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Link>
                </Button>
              </div>
            </div>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Unit Details</CardTitle>
              <CardDescription>Information about this unit type</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Room Type</h3>
                  <p className="mt-1 text-lg">{formatRoomType(unit.room_type)}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Facility</h3>
                  <p className="mt-1 text-lg">{unit.facility?.name || 'Unknown facility'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Bedrooms</h3>
                  <p className="mt-1 text-lg">{unit.bedrooms}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Bathrooms</h3>
                  <p className="mt-1 text-lg">{unit.bathrooms}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Monthly Cost</h3>
                  <p className="mt-1 text-lg font-bold">{formatCurrency(unit.monthly_cost_cents)}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Available Units</h3>
                  <p className="mt-1">
                    <Badge variant={unit.available_count > 0 ? "success" : "secondary"} className="text-lg">
                      {unit.available_count}
                    </Badge>
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Square Footage</h3>
                  <p className="mt-1 text-lg">{unit.square_feet || 'Not specified'} sq ft</p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" asChild>
                <Link href={`/facility_owner/facilities/${unit.facility_id}`}>
                  <FiHome className="mr-2 h-4 w-4" />
                  View Facility
                </Link>
              </Button>
              <Button asChild>
                <Link href={`/facility_owner/units/${unit.id}/edit`}>
                  <FiEdit2 className="mr-2 h-4 w-4" />
                  Edit Unit
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
